import { FC, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { Icon } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { useContinueToNextPathStep } from 'src/hooks/useContinueToNextPathStep';
import useSafePush from 'src/hooks/useSafePush';
import { LearnablePronunciationData } from 'src/api/userDictionary/types';
import { PronunciationTokenUpsell } from 'src/apps/Monetisation/BlobExperienceUpsell';
import { PronunciationLimitUpsell } from 'src/apps/Monetisation/PronunciationLimitUpsell';
import { PreCheckpointScreen } from 'src/apps/Path/PreCheckpointScreen';
import { useSessionSpeakerClips } from '../../hooks/useSessionSpeakerClips';
import { SpeakerMosaic } from '../SpeakerMosaic';
import * as S from './styles';

const msgs = defineMessages({
  freeLesson: {
    id: 'pronunciation_session_one_free_lesson_a_day',
    defaultMessage: 'One free Pronunciation Lesson a day',
  },
  title: {
    id: 'pronunciation_session_preview_title',
    defaultMessage: 'Say it the way they say it',
  },
  description: {
    id: 'pronunciation_session_preview_description',
    defaultMessage: 'Practice speaking out loud with a little help from your native speakers',
  },
  cta: { id: 'scenario_button_start_lesson' },
  continueLearning: { id: 'learnq_previewScreen_learn_cont_CTA' },
  skipSession: {
    id: 'blob_pronunciation_skip_session',
    defaultMessage: 'Skip pronunciation session',
  },
});

/**
 * SPIKE: the session's own native speakers form the backdrop of the intro,
 * so the learner meets the faces they're about to imitate.
 */
export const ImmersivePreviewScreen: FC<{
  learnables: LearnablePronunciationData[];
  isLocked: boolean;
  showFreeLessonPill: boolean;
  onContinue: () => void;
  pathwayNodeId: number | null;
  languagePairId: number | null;
  onSkipSession?: () => void;
  checkAccess?: () => Promise<boolean>;
}> = ({
  learnables,
  isLocked,
  showFreeLessonPill,
  onContinue,
  pathwayNodeId,
  languagePairId,
  onSkipSession,
  checkAccess,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const { safePush } = useSafePush();
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [hasDismissedUpsell, setHasDismissedUpsell] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);

  const speakerClips = useSessionSpeakerClips(learnables);

  const {
    continueToNextStep,
    isResolving,
    pendingCheckpointUrl,
    confirmCheckpoint,
    isLaunchingCheckpoint,
  } = useContinueToNextPathStep({
    languagePairId,
    pathwayNodeId,
    pathwayCheckpointId: null,
    advanceBy: 'skip',
  });

  // Path steps and Home launch with `source=path`, the blob Path header with
  // `source=path_feature_card`; everything else reaches here from Speak.
  const isPathLaunch =
    pathwayNodeId !== null ||
    router.query.source === 'path' ||
    router.query.source === 'path_feature_card';

  const handleStart = async () => {
    if (checkAccess) {
      setIsCheckingAccess(true);
      try {
        if (!(await checkAccess())) {
          setShowUpsellModal(true);
          return;
        }
        onContinue();
      } catch {
        // The session displays an access error with retry/skip actions.
      } finally {
        setIsCheckingAccess(false);
      }
      return;
    }
    if (isLocked) {
      setShowUpsellModal(true);
      return;
    }
    onContinue();
  };

  const handleUpsellDismiss = () => {
    setShowUpsellModal(false);
    setHasDismissedUpsell(true);
  };

  const handleContinueLearning = () => {
    if (isPathLaunch) {
      continueToNextStep();
      return;
    }
    void safePush(ROUTES.speak);
  };

  if (pendingCheckpointUrl) {
    return (
      <PreCheckpointScreen
        onContinue={confirmCheckpoint}
        isContinuing={isLaunchingCheckpoint}
        onBack={() => safePush(ROUTES.path)}
      />
    );
  }

  return (
    <S.Screen>
      <SpeakerMosaic clips={speakerClips} />

      <S.Panel>
        {showFreeLessonPill && (
          <S.FreeLessonPill>
            <Icon name="lightning" />
            {intl.formatMessage(msgs.freeLesson)}
          </S.FreeLessonPill>
        )}
        <S.Title>{intl.formatMessage(msgs.title)}</S.Title>
        <S.Description>{intl.formatMessage(msgs.description)}</S.Description>
        <S.WordChips>
          {learnables.map(learnable => (
            <S.WordChip key={learnable.learnableIdKey}>
              <S.ChipTarget dir="auto">{learnable.mainTargetValue}</S.ChipTarget>
              <S.ChipSource dir="auto">{learnable.sourceDefinitionValue}</S.ChipSource>
            </S.WordChip>
          ))}
        </S.WordChips>
        <S.StartButton
          type="button"
          onClick={() => void handleStart()}
          disabled={isCheckingAccess}
          aria-busy={isCheckingAccess}
        >
          {intl.formatMessage(msgs.cta)}
        </S.StartButton>
        {onSkipSession ? (
          <S.ContinueLearningLink type="button" onClick={onSkipSession}>
            {intl.formatMessage(msgs.skipSession)}
          </S.ContinueLearningLink>
        ) : isLocked && hasDismissedUpsell ? (
          <S.ContinueLearningLink
            type="button"
            onClick={handleContinueLearning}
            disabled={isResolving}
          >
            {intl.formatMessage(msgs.continueLearning)}
          </S.ContinueLearningLink>
        ) : null}
      </S.Panel>

      {showUpsellModal && (
        <PronunciationTokenUpsell
          onContinue={handleUpsellDismiss}
          isSession={!!checkAccess || !!onSkipSession}
          legacy={
            <PronunciationLimitUpsell trigger="start_lesson" onContinue={handleUpsellDismiss} />
          }
        />
      )}
    </S.Screen>
  );
};
